import type { AuthTokens, StoredAuth } from "@/types/session";
import { API_BASE_URL } from "./consts";

class AuthService {
	private static readonly AUTH_KEY = "auth_tokens";
	private static readonly IMP_KEY = "impersonation_auth";
	private refreshPromise: Promise<boolean> | null = null;

	// Store tokens securely
	setTokens(tokens: AuthTokens): void {
		const expiresAt = Date.now() + tokens.expiry * 1000;
		const storedAuth: StoredAuth = {
			...tokens,
			expiresAt,
		};

		localStorage.setItem(AuthService.AUTH_KEY, JSON.stringify(storedAuth));
	}

	// Store impersonation token in sessionStorage (tab-scoped)
	setImpersonationToken(token: string, expiry: number): void {
		const storedAuth: StoredAuth = {
			authToken: token,
			refreshToken: "",
			expiry,
			expiresAt: Date.now() + expiry * 1000,
		};
		sessionStorage.setItem(
			AuthService.IMP_KEY,
			JSON.stringify(storedAuth),
		);
	}

	clearImpersonationToken(): void {
		sessionStorage.removeItem(AuthService.IMP_KEY);
	}

	isImpersonating(): boolean {
		return sessionStorage.getItem(AuthService.IMP_KEY) !== null;
	}

	// Get current auth token
	async getAuthToken(): Promise<string | null> {
		const auth = this.getStoredAuth();
		if (!auth) {
			return null;
		}

		return auth.authToken;
	}

	// Get refresh token
	getRefreshToken(): string | null {
		const auth = this.getStoredAuth();
		return auth?.refreshToken || null;
	}

	// Get stored auth data — impersonation (sessionStorage) takes priority
	private getStoredAuth(): StoredAuth | null {
		// Check sessionStorage first (impersonation token, tab-scoped)
		const impStored = sessionStorage.getItem(AuthService.IMP_KEY);
		if (impStored) {
			try {
				return JSON.parse(impStored) as StoredAuth;
			} catch {
				// fall through
			}
		}

		// Fall back to localStorage (normal auth)
		const stored = localStorage.getItem(AuthService.AUTH_KEY);
		if (!stored) return null;

		try {
			return JSON.parse(stored) as StoredAuth;
		} catch {
			return null;
		}
	}

	// Check if user is authenticated (synchronous check)
	isAuthenticated(): boolean {
		const auth = this.getStoredAuth();
		return auth !== null;
	}

	// Ensure valid token exists (async, refreshes if needed)
	async ensureValidToken(): Promise<boolean> {
		const token = await this.getAuthToken();
		return token !== null;
	}

	// Refresh tokens (prevents multiple simultaneous refresh requests)
	async refreshTokens(): Promise<boolean> {
		// Impersonation sessions cannot be refreshed
		if (this.isImpersonating()) {
			return false;
		}

		// If refresh is already in progress, wait for it
		if (this.refreshPromise) {
			return this.refreshPromise;
		}

		const refreshToken = this.getRefreshToken();
		if (!refreshToken) {
			this.clearTokens();
			return false;
		}

		this.refreshPromise = (async () => {
			try {
				// NOTE: We're not using apiService here to avoid circular dependency
				const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ refresh_token: refreshToken }),
				});

				if (!response.ok) {
					throw new Error("Refresh failed");
				}

				// Server returns snake_case; map to AuthTokens interface
				const raw = await response.json();
				this.setTokens({
					authToken: raw.access_token,
					refreshToken: raw.refresh_token,
					expiry: raw.expires_in ?? 1800,
				});
				return true;
			} catch (error) {
				console.error("Token refresh failed:", error);
				this.clearTokens();
				return false;
			} finally {
				this.refreshPromise = null;
			}
		})();

		return this.refreshPromise;
	}

	clearTokens(): void {
		localStorage.removeItem(AuthService.AUTH_KEY);
		sessionStorage.removeItem(AuthService.IMP_KEY);
		this.refreshPromise = null;
	}
}

export const authService = new AuthService();
